import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Mail, RefreshCw, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../services/api';

const VerifyEmailPending = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || '';
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [cooldown, setCooldown] = useState(0);

  // Masquer l'email : j***@gmail.com
  const maskEmail = (email) => {
    if (!email || !email.includes('@')) return '***@***.***';
    const [local, domain] = email.split('@');
    if (local.length <= 1) return `${local}***@${domain}`;
    return `${local[0]}***@${domain}`;
  };

  // Cooldown timer
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  // Si pas d'email dans le state, rediriger vers inscription
  useEffect(() => {
    if (!email) {
      navigate('/inscription');
    }
  }, [email, navigate]);

  const handleResend = async () => {
    if (cooldown > 0 || resendLoading) return;
    setResendLoading(true);
    setResendError('');
    setResendSuccess(false);

    try {
      await api.post('/auth/resend-verification', null, { params: { email } });
      setResendSuccess(true);
      setCooldown(60);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err) {
      const detail = err.response?.data?.detail || "Erreur lors du renvoi.";
      setResendError(detail);
      // Si throttle, extraire le temps restant
      if (err.response?.status === 429) {
        const match = detail.match(/(\d+)/);
        if (match) setCooldown(parseInt(match[1]));
      }
    } finally {
      setResendLoading(false);
    }
  };

  if (!email) return null;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="verify-email-pending-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-[#1A1A1D] p-8 rounded-lg metallic-border cyber-glow text-center">
          {/* Icône email */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="inline-block p-5 bg-[#00FFFF]/10 rounded-full mb-6"
          >
            <Mail className="h-14 w-14 text-[#00FFFF]" />
          </motion.div>

          <h1 className="text-2xl font-bold text-white mb-3" data-testid="verify-title">
            Vérifiez votre email
          </h1>

          <p className="text-[#B8B8BA] mb-2">
            Un email de vérification a été envoyé à :
          </p>
          <p className="text-[#00FFFF] font-semibold text-lg mb-6" data-testid="masked-email">
            {maskEmail(email)}
          </p>

          {/* Instructions */}
          <div className="bg-[#222226] rounded-lg p-5 mb-6 text-left space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-[#00FF88] flex-shrink-0 mt-0.5" />
              <span className="text-[#E8E8E8] text-sm">Vérifiez votre boîte de réception principale</span>
            </div>
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-[#FFB800] flex-shrink-0 mt-0.5" />
              <span className="text-[#E8E8E8] text-sm">Vérifiez votre dossier <strong className="text-white">Spam / Courrier indésirable</strong></span>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-[#B8B8BA] flex-shrink-0 mt-0.5" />
              <span className="text-[#E8E8E8] text-sm">L'email peut prendre jusqu'à 5 minutes pour arriver</span>
            </div>
          </div>

          {/* Messages de succès / erreur */}
          {resendSuccess && (
            <div className="bg-[#00FF88]/10 border border-[#00FF88]/30 rounded-lg p-3 mb-4" data-testid="resend-success-msg">
              <p className="text-[#00FF88] text-sm">Email de vérification renvoyé avec succès !</p>
            </div>
          )}
          {resendError && (
            <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C]/30 rounded-lg p-3 mb-4" data-testid="resend-error-msg">
              <p className="text-[#FF3B5C] text-sm">{resendError}</p>
            </div>
          )}

          {/* Bouton renvoyer */}
          <button
            onClick={handleResend}
            disabled={cooldown > 0 || resendLoading}
            className="w-full flex items-center justify-center gap-2 bg-[#222226] border border-[#3A3A3D] hover:border-[#00FFFF] text-white py-3 px-6 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-4"
            data-testid="resend-btn"
          >
            <RefreshCw className={`h-4 w-4 ${resendLoading ? 'animate-spin' : ''}`} />
            {cooldown > 0 
              ? `Renvoyer l'email (${cooldown}s)` 
              : resendLoading 
                ? 'Envoi en cours...' 
                : "Renvoyer l'email de vérification"
            }
          </button>

          {/* Lien vers connexion */}
          <button
            onClick={() => navigate('/connexion')}
            className="text-[#00FFFF] text-sm hover:underline"
            data-testid="back-to-login"
          >
            Aller à la page de connexion
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmailPending;
